import classNames from 'classnames'
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useOutletContext } from 'react-router-dom'

import { useNavigate } from '@lib/react-router-dom'

import { getLogoForAccount } from '@pages/Trade/TradingAccountInfo/utils/getLogoForAccount'
import { LeverageLink } from '@pages/Traders-Room/Trading-Accounts/Demo/DemoTradingAccountsCards'
import { TradingAccountsOutletCtx } from '@pages/Traders-Room/Trading-Accounts/TradingAccounts.page'
import { MobileTradeButtonLinkWrapper } from '@pages/Traders-Room/Trading-Accounts/components/TradeButtonLinkWrapper'
import { TradingAccountActionsDropdown } from '@pages/Traders-Room/Trading-Accounts/components/TradingAccountActionsDropdown'

import { useModal } from '@context/ModalsContext'

import { SensitiveData } from '@ui/Privacy/SensitiveData'
import { Body1, Body2, Caption1 } from '@ui/Typography'
import { ActionIcon } from '@ui/components/ActionIcon'
import { Badge } from '@ui/components/Badge/Badge'
import { Button, IconButton } from '@ui/components/Button'
import { Link } from '@ui/components/Link'
import { Tooltip } from '@ui/components/Tooltip'
import { ArrowCircleIcon } from '@ui/icons/ArrowCircle.icon'
import { CaretIcon } from '@ui/icons/Caret.icon'
import { CopyIcon } from '@ui/icons/Copy.icon'
import { InfoIcon } from '@ui/icons/Info.icon'
import { KebabIcon } from '@ui/icons/Kebab.icon'

import { PlatformType, isPlatformTypeDisabled } from '@model/PlatformTypeDto'
import {
  getDisplayPlatformName,
  isMT4PlatformType,
  isMT5PlatformType,
  isTMTPlatformType,
  isTradingViewPlatformType,
} from '@model/PlatformTypeEnum'
import {
  DemoTradingAccount,
  isTradingAccountStatusApproved,
  isTradingAccountStatusCloseOnly,
  isTradingAccountStatusReadOnly,
} from '@model/TradingAccount'

import { useCopy } from '@hooks/useCopy'
import { useFormatNumber } from '@hooks/useFormatNumber'
import { useScrollToElementIds } from '@hooks/useScrollToElementIds'

import { Box } from '@shared/components/Box'
import { InformationModal } from '@shared/modals/Information.modal'

import { TickmillTraderUsername, TradingAccountNumber } from './MobileTradingAccountCard'

import styles from './MobileTradingAccountCard.module.scss'

const isDemoWebTraderAvailable = (ta: DemoTradingAccount) =>
  isTradingAccountStatusApproved(ta.status) || isTradingAccountStatusCloseOnly(ta.status)

const expandedStateByCardId = new Map<string, boolean>()

interface DetailRowProps {
  label: React.ReactNode
  value: React.ReactNode
  inactive?: boolean
}

const DetailRow: FC<DetailRowProps> = ({ label, value, inactive }) => (
  <div className={classNames(styles.subtitle, { [styles.inactive]: inactive })}>
    <Body2 className={styles.label}>{label}</Body2>
    <Body2 className={styles.value}>{value}</Body2>
  </div>
)

interface AccountHeaderProps {
  tradingAccount: DemoTradingAccount
  currencyId: string
  accountName: string
  formattedBalance: string
  isActive: boolean
}

const AccountHeader: FC<AccountHeaderProps> = ({
  tradingAccount,
  accountName,
  formattedBalance,
  isActive,
}) => {
  const { t } = useTranslation()
  return (
    <Box flex gap='x12'>
      <Box className={styles.logoCircle}>{getLogoForAccount(tradingAccount)}</Box>
      <Box textAlign='start'>
        <Box flex gap='x8' align='center' wrap>
          <Caption1 isParagraph>{accountName}</Caption1>
          {!isActive &&
            !isTradingAccountStatusReadOnly(tradingAccount.status) &&
            !isTradingAccountStatusCloseOnly(tradingAccount.status) && (
              <Badge type='yellow' size='xxs'>
                {t('Deactivated')}
              </Badge>
            )}
          {isTradingAccountStatusReadOnly(tradingAccount.status) && (
            <Badge type='yellow' size='xxs' contrast='low'>
              {t('Trading Account.Read Only')}
            </Badge>
          )}
          {isTradingAccountStatusCloseOnly(tradingAccount.status) && (
            <Badge type='yellow' size='xxs' contrast='low'>
              {t('Trading Account.Close Only')}
            </Badge>
          )}
        </Box>
        <Box>
          <Body1 semibold>
            <SensitiveData>{formattedBalance}</SensitiveData>
          </Body1>
        </Box>
      </Box>
    </Box>
  )
}

interface AccountActionsProps {
  tradingAccount: DemoTradingAccount
  isMT4: boolean
  isMT5: boolean
  handleChangePasswordRestricted: () => void
  handleChangeInvestorPasswordRestricted: () => void
  openPassInfoModal: () => void
  handleSetSelectedTradingAccount: (tradingAccount: DemoTradingAccount) => void
  openResetPasswordModal: () => void
  handleResetPasswordRestricted: () => void
}

const AccountActions: FC<AccountActionsProps> = ({
  tradingAccount,
  isMT4,
  isMT5,
  handleChangePasswordRestricted,
  handleChangeInvestorPasswordRestricted,
  openPassInfoModal,
  handleResetPasswordRestricted,
  handleSetSelectedTradingAccount,
  openResetPasswordModal,
}) => {
  const { t } = useTranslation()

  return (
    <Box flex gap='x8' mt='x16'>
      <Box fullWidth>{<MobileTradeButtonLinkWrapper tradingAccount={tradingAccount} />}</Box>
      <Box fullWidth>
        <Tooltip
          fullWidth
          disabled={isTradingAccountStatusApproved(tradingAccount.status)}
          content={t('This action is not available')}
        >
          <Link
            to={
              isTradingAccountStatusApproved(tradingAccount.status)
                ? `/dashboard/traders-room/trading-accounts/${tradingAccount.id}/demo/add-balance`
                : '#'
            }
            onClick={(e) => {
              if (!isTradingAccountStatusApproved(tradingAccount.status)) e.preventDefault()
            }}
          >
            <Button.Secondary
              size='md'
              fullWidth
              disabled={!isTradingAccountStatusApproved(tradingAccount.status)}
            >
              {`${t('Trading Account.Add')} ${t('Trading Account.Balance')}`}
            </Button.Secondary>
          </Link>
        </Tooltip>
      </Box>
      <Box>
        {isMT4 || isMT5 ? (
          <IconButton.Ghost size='md' icon={KebabIcon} onClick={openPassInfoModal} />
        ) : (
          <TradingAccountActionsDropdown
            tradingAccount={tradingAccount}
            restrictions={[]}
            onChangePasswordRestrictedClick={handleChangePasswordRestricted}
            onChangeInvestorPasswordRestrictedClick={handleChangeInvestorPasswordRestricted}
            onResetPasswordClick={() => {
              handleSetSelectedTradingAccount(tradingAccount)
              openResetPasswordModal()
            }}
            onResetPasswordRestrictedClick={handleResetPasswordRestricted}
          />
        )}
      </Box>
    </Box>
  )
}

AccountActions.displayName = 'AccountActions'

interface DemoTradingAccountCardProps {
  tradingAccount: DemoTradingAccount
  isTradingInActive?: boolean
  platformTypes?: PlatformType[]
  onDisplayDynamicLeverageInfo?(visible: boolean): void
  cardId?: string
  onLeverageChange?: (args: { tradingAccount: DemoTradingAccount }) => void
}

export const MobileDemoTradingAccountCard: React.FC<DemoTradingAccountCardProps> = (props) => {
  const { tradingAccount, platformTypes, cardId } = props

  const {
    handleSetSelectedTradingAccount,
    handleChangePasswordRestricted,
    handleResetPasswordRestricted,
    handleChangeInvestorPasswordRestricted,
    openResetPasswordModal,
  } = useOutletContext<TradingAccountsOutletCtx>()

  const { t } = useTranslation()
  const { scrollIntoView } = useScrollToElementIds()
  const { formatMoney } = useFormatNumber()

  const passInfoModal = useModal(InformationModal, {
    description: t('Trading Account.The password for demo trading accounts'),
  })

  const [expanded, setExpanded] = useState(() => {
    if (cardId) {
      return expandedStateByCardId.get(cardId) ?? false
    }
    return false
  })

  useEffect(() => {
    if (cardId) {
      expandedStateByCardId.set(cardId, expanded)
    }
  }, [cardId, expanded])

  const computedData = useMemo(() => {
    const isPlatformDisabled =
      platformTypes && isPlatformTypeDisabled(platformTypes, tradingAccount.platformType.id)
    const isTradeDisabled = !isDemoWebTraderAvailable(tradingAccount)
    const isActive = !isPlatformDisabled && !isTradeDisabled
    const isMT4 = isMT4PlatformType(tradingAccount.platformType.id)
    const isMT5 = isMT5PlatformType(tradingAccount.platformType.id)
    const formattedBalance = formatMoney(
      tradingAccount.platformOverview?.balance,
      tradingAccount.currency.id
    )

    return {
      isPlatformDisabled,
      isTradeDisabled,
      isActive,
      isMT4,
      isMT5,
      formattedBalance,
    }
  }, [tradingAccount, platformTypes, formatMoney])

  const handleToggleExpand = useCallback(() => {
    if (!expanded && cardId) {
      scrollIntoView([cardId])
    }
    setExpanded((prev) => !prev)
  }, [expanded, cardId, scrollIntoView])

  const openPassInfoModal = useCallback(() => {
    passInfoModal.open({ title: `${tradingAccount.name}` })
  }, [passInfoModal, tradingAccount])

  return (
    <Box
      id={cardId}
      className={classNames(styles.card, { [styles.inactive]: !computedData.isActive })}
      radius='x32'
      my='x16'
    >
      <Box flex justify='space-between'>
        <AccountHeader
          tradingAccount={tradingAccount}
          currencyId={tradingAccount.currency.id}
          accountName={tradingAccount.nickname || tradingAccount.name}
          formattedBalance={computedData.formattedBalance}
          isActive={computedData.isActive}
        />
        <IconButton.Ghost
          aria-label={expanded ? t('Collapse details') : t('Expand details')}
          aria-expanded={expanded}
          onClick={handleToggleExpand}
          icon={expanded ? CaretIcon.Up : CaretIcon.Down}
        />
      </Box>
      {expanded && <Expanded {...props} computedData={computedData} />}

      {computedData.isActive && (
        <AccountActions
          tradingAccount={tradingAccount}
          isMT4={computedData.isMT4}
          isMT5={computedData.isMT5}
          handleChangePasswordRestricted={handleChangePasswordRestricted}
          handleChangeInvestorPasswordRestricted={handleChangeInvestorPasswordRestricted}
          openPassInfoModal={openPassInfoModal}
          handleSetSelectedTradingAccount={handleSetSelectedTradingAccount}
          openResetPasswordModal={openResetPasswordModal}
          handleResetPasswordRestricted={handleResetPasswordRestricted}
        />
      )}
    </Box>
  )
}

interface ExpandedProps extends DemoTradingAccountCardProps {
  computedData: {
    isPlatformDisabled: boolean | undefined
    isTradeDisabled: boolean
    isActive: boolean
    isMT4: boolean
    isMT5: boolean
    formattedBalance: string
  }
}

const Expanded: React.FC<ExpandedProps> = ({
  tradingAccount,
  onDisplayDynamicLeverageInfo,
  isTradingInActive = false,
  computedData,
  onLeverageChange,
}) => {
  const { t } = useTranslation()
  const { formatMoney } = useFormatNumber()
  const navigate = useNavigate()

  const { isMT4, isMT5, isActive } = computedData
  const isDynamicLeverage = tradingAccount.leverage?.name === 'Dynamic'
  const equity = formatMoney(tradingAccount.platformOverview.equity, tradingAccount.currency.id)

  const TMTInfoModal = useModal(InformationModal, {
    description: t(
      'Trading Account.You can use your Tickmill Trader login credentials to access the TradingView platform'
    ),
    primaryLabel: t('Trading Account.Download'),
    onPrimaryClick: () => {
      TMTInfoModal.close()
      navigate('/dashboard/traders-room/platforms/tickmilltrader/webtrader')
    },
  })
  const TVInfoModal = useModal(InformationModal, {
    description: t(
      'Trading Account.You can use your TradingView login credentials to access the Tickmill Trader Mobile App'
    ),
    primaryLabel: t('Trading Account.Download'),
    onPrimaryClick: () => {
      TVInfoModal.close()
      navigate('/dashboard/traders-room/platforms/tradingview')
    },
  })

  const openPlatformModal = useCallback(() => {
    if (isTMTPlatformType(tradingAccount.platformType.id)) TMTInfoModal.open()
    else if (isTradingViewPlatformType(tradingAccount.platformType.id)) TVInfoModal.open()
  }, [TMTInfoModal, TVInfoModal, tradingAccount.platformType.id])

  return (
    <React.Fragment>
      <Box flex direction='column' gap='x16'>
        <Box className={styles.accountDetails} p='x6' radius='x12' mt='x16'>
          <DetailRow
            label={t('Trading Account.Account Type')}
            value={tradingAccount.accountType?.name}
            inactive={!isActive}
          />
          <DetailRow
            label={t('Trading Account.Leverage')}
            value={
              <Box flex align='center'>
                <Box>
                  {tradingAccount.platformType.dynamicLeverage ? (
                    <LeverageLink
                      to=''
                      tradingAccount={tradingAccount}
                      onClick={(e) => {
                        e.stopPropagation()
                        onLeverageChange?.({ tradingAccount })
                      }}
                    />
                  ) : (
                    <LeverageLink
                      to=''
                      state={{ tradingAccountId: tradingAccount.id }}
                      tradingAccount={tradingAccount}
                      onClick={(e) => {
                        e.stopPropagation()
                        onLeverageChange?.({ tradingAccount })
                      }}
                    />
                  )}
                </Box>
                {isDynamicLeverage &&
                  !isTradingInActive &&
                  tradingAccount.platformType.dynamicLeverage && (
                    <Box flex ml='x4'>
                      <ActionIcon
                        icon={InfoIcon}
                        size='x20'
                        onClick={() => onDisplayDynamicLeverageInfo?.(true)}
                      />
                    </Box>
                  )}
              </Box>
            }
            inactive={!isActive}
          />
          <DetailRow
            label={t('Trading Account.Equity')}
            value={<SensitiveData>{equity}</SensitiveData>}
            inactive={!isActive}
          />
          {tradingAccount.introducingBroker?.name && (
            <DetailRow
              label={t('Trading Account.IB Code')}
              value={tradingAccount.introducingBroker?.name}
              inactive={!isActive}
            />
          )}
          <DetailRow
            label={t('Trading Account.Platform')}
            value={
              <Box flex align='center' gap='x4'>
                {getDisplayPlatformName(tradingAccount.platformType)}
                {!isTradingInActive &&
                  (isTMTPlatformType(tradingAccount.platformType.id) ||
                    isTradingViewPlatformType(tradingAccount.platformType.id)) && (
                    <ActionIcon icon={ArrowCircleIcon.Down} onClick={openPlatformModal} />
                  )}
                {/*{!isTradingInActive && isMT4 && (*/}
                {/*  <ActionIcon*/}
                {/*    icon={ArrowCircleIcon.Down}*/}
                {/*    onClick={() => navigate('/dashboard/traders-room/platforms/mt4')}*/}
                {/*  />*/}
                {/*)}*/}
                {/*{!isTradingInActive && isMT5 && (*/}
                {/*  <ActionIcon*/}
                {/*    icon={ArrowCircleIcon.Down}*/}
                {/*    onClick={() => navigate('/dashboard/traders-room/platforms/mt5')}*/}
                {/*  />*/}
                {/*)}*/}
              </Box>
            }
            inactive={!isActive}
          />
        </Box>
        {(isTMTPlatformType(tradingAccount?.platformType.id) ||
          isTMTPlatformType(tradingAccount?.parentPlatformType?.id)) && (
          <TickmillTraderUsername tradingAccount={tradingAccount} />
        )}

        {!isTradingInActive && !(isMT4 || isMT5) && (
          <TradingAccountNumber tradingAccount={tradingAccount} />
        )}
        {!isTradingInActive && (isMT4 || isMT5) && (
          <DemoTradingAccountServer tradingAccount={tradingAccount} />
        )}
        {!isTradingInActive && (isMT4 || isMT5) && (
          <DemoTradingAccountMTLogin tradingAccount={tradingAccount} />
        )}
      </Box>
    </React.Fragment>
  )
}

interface ExpandableDemoTradingAccountProps {
  tradingAccount: DemoTradingAccount
}

export const DemoTradingAccountMTLogin: React.FC<ExpandableDemoTradingAccountProps> = ({
  tradingAccount,
}) => {
  const { t } = useTranslation()
  const { copy } = useCopy()

  if (!tradingAccount?.platformOverview?.login) {
    return null
  }

  return (
    <Box className={styles.subtitle} align='center'>
      <Body2 className={styles.label}>{t('Trading Account.MT Login')}</Body2>
      <Body2 className={styles.value}>
        <Box className={styles.usernameWrapper} gap='x8' align='center'>
          <Body2>{tradingAccount.platformOverview.login}</Body2>
          <Body2 className={styles.usernameIcon}>
            <IconButton.Ghost
              icon={CopyIcon}
              size='sm'
              onClick={() => copy(tradingAccount.platformOverview.login)}
            />
          </Body2>
        </Box>
      </Body2>
    </Box>
  )
}

export const DemoTradingAccountServer: React.FC<ExpandableDemoTradingAccountProps> = ({
  tradingAccount,
}) => {
  const { t } = useTranslation()
  const { copy } = useCopy()

  if (!tradingAccount?.server?.publicName) {
    return null
  }

  return (
    <Box className={styles.subtitle} align='center'>
      <Body2 className={styles.label}>{t('Trading Account.Server')}</Body2>
      <Body2 className={styles.value}>
        <Box className={styles.usernameWrapper} gap='x8' align='center'>
          <Body2>{tradingAccount.server.publicName}</Body2>
          <Body2 className={styles.usernameIcon}>
            <IconButton.Ghost
              icon={CopyIcon}
              size='sm'
              onClick={() => copy(tradingAccount.server.publicName)}
            />
          </Body2>
        </Box>
      </Body2>
    </Box>
  )
}
